/**
 * =============================================================================
 * Componente: CommissionStatusBadge
 * Versão: 1.0.0
 * Release: 2026-05-02 — Util compartilhado para status de COMISSÃO
 *
 * Descrição:
 *   Padroniza a exibição do status de uma comissão (pending / approved /
 *   paid / cancelled / reversed) em qualquer tela do sistema.
 *
 *   Visual oficial: "dot colorido + texto" — mesmo padrão de
 *   `ReferralStatusBadge`.
 *
 *   Também exporta `COMMISSION_STATUS_CONFIG` para reuso da cor em barras
 *   laterais e elementos auxiliares.
 * =============================================================================
 */
import { cn } from '@/lib/utils';

export type CommissionStatus =
  | 'pending'
  | 'approved'
  | 'paid'
  | 'cancelled'
  | 'reversed';

export const COMMISSION_STATUS_CONFIG: Record<
  CommissionStatus,
  { label: string; dot: string; bar: string }
> = {
  // Âmbar: gerada, aguardando aprovação
  pending:   { label: 'Pendente',  dot: 'bg-amber-500',           bar: 'bg-amber-500' },
  // Azul: aprovada, aguardando pagamento
  approved:  { label: 'Aprovada',  dot: 'bg-blue-500',            bar: 'bg-blue-500' },
  // Verde: paga ao afiliado
  paid:      { label: 'Paga',      dot: 'bg-green-500',           bar: 'bg-green-500' },
  // Vermelho: cancelada
  cancelled: { label: 'Cancelada', dot: 'bg-red-500',             bar: 'bg-red-500' },
  // Cinza: revertida (estorno)
  reversed:  { label: 'Revertida', dot: 'bg-muted-foreground/40', bar: 'bg-muted-foreground/40' },
};

interface CommissionStatusBadgeProps {
  status: string | null | undefined;
  className?: string;
  fallback?: React.ReactNode;
}

export function CommissionStatusBadge({
  status,
  className,
  fallback,
}: CommissionStatusBadgeProps) {
  const cfg = status
    ? COMMISSION_STATUS_CONFIG[status as CommissionStatus]
    : null;

  if (!cfg) {
    return (
      <>{fallback ?? <span className="text-xs text-muted-foreground">—</span>}</>
    );
  }

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 text-xs font-medium text-foreground whitespace-nowrap',
        className,
      )}
    >
      <span aria-hidden className={cn('h-2 w-2 rounded-full shrink-0', cfg.dot)} />
      {cfg.label}
    </span>
  );
}
